import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, ChevronLeft } from "lucide-react";
import {
  useAgentWorkflowTriggersStore,
  TriggerDefinition,
} from "@/store/agentWorkflowTriggersStore";
import { useAgentWorkflowAppsStore, App } from "@/store/agentWorkflowAppsStore";

interface SelectTriggerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectTrigger: (trigger: TriggerDefinition) => void;
}

export function SelectTriggerModal({
  open,
  onOpenChange,
  onSelectTrigger,
}: SelectTriggerModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const triggers = useAgentWorkflowTriggersStore((state) => state.triggers);
  const triggerState = useAgentWorkflowTriggersStore(
    (state) => state.triggerState,
  );
  const fetchAppTriggers = useAgentWorkflowTriggersStore(
    (state) => state.fetchAppTriggers,
  );
  const getTriggerApps = useAgentWorkflowAppsStore(
    (state) => state.getTriggerApps,
  );
  const appState = useAgentWorkflowAppsStore((state) => state.appState);
  const fetchTriggerApps = useAgentWorkflowAppsStore(
    (state) => state.fetchTriggerApps,
  );
  const triggerAppIds = useAgentWorkflowAppsStore(
    (state) => state.triggerAppIds,
  );

  useEffect(() => {
    if (open && triggerAppIds.length === 0 && !appState.isLoadingGet) {
      fetchTriggerApps();
    }
  }, [open, triggerAppIds.length, appState.isLoadingGet, fetchTriggerApps]);

  useEffect(() => {
    if (selectedApp) {
      fetchAppTriggers(selectedApp.id);
    }
  }, [selectedApp, fetchAppTriggers]);

  const appsArray = getTriggerApps();
  const filteredApps = appsArray.filter((app) => {
    return (
      app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const triggersArray = selectedApp
    ? selectedApp.triggerIds
        .map((id) => triggers.get(id))
        .filter(
          (trigger): trigger is TriggerDefinition => trigger !== undefined,
        )
    : [];
  const filteredTriggers = triggersArray.filter(
    (trigger) =>
      trigger.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      trigger.description.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleSelectTrigger = (trigger: TriggerDefinition) => {
    onSelectTrigger(trigger);
    onOpenChange(false);
    setSearchQuery("");
    setSelectedApp(null);
  };

  const handleSelectApp = (app: App) => {
    setSelectedApp(app);
    setSearchQuery("");
  };

  const handleBack = () => {
    setSelectedApp(null);
    setSearchQuery("");
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedApp(null);
      setSearchQuery("");
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-2xl mx-auto">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg">
            {selectedApp ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  className="h-9 w-9 sm:h-auto sm:w-auto p-1"
                >
                  <ChevronLeft className="h-5 w-5 sm:h-4 sm:w-4" />
                </Button>
                <span className="truncate">{selectedApp.name}</span>
              </div>
            ) : (
              "Add Trigger"
            )}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {selectedApp
              ? "Select a trigger from this app"
              : "Select an app to browse its triggers"}
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-10 sm:h-9"
          />
        </div>

        <ScrollArea className="h-[50vh] sm:h-[400px] pr-4">
          {!selectedApp ? (
            appState.isLoadingGet ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-muted-foreground">Loading apps...</div>
              </div>
            ) : appState.getError ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-destructive">{appState.getError}</div>
              </div>
            ) : filteredApps.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-muted-foreground">
                  {searchQuery ? "No apps found" : "No apps available"}
                </div>
              </div>
            ) : (
              <div className="grid gap-2">
                {filteredApps.map((app) => (
                  <Button
                    key={app.id}
                    variant="outline"
                    className="h-auto justify-start p-3 sm:p-4 text-left active:bg-muted/70 w-full overflow-hidden"
                    onClick={() => handleSelectApp(app)}
                  >
                    <div className="flex items-start gap-2 sm:gap-3 w-full min-w-0">
                      {app.imageUrl && (
                        <img
                          src={app.imageUrl}
                          alt={app.name}
                          className="h-8 w-8 rounded object-contain flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="font-medium text-sm sm:text-base truncate">
                          {app.name}
                        </div>
                        <div className="text-xs sm:text-sm text-muted-foreground line-clamp-2">
                          {app.description}
                        </div>
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            )
          ) : triggerState.isLoadingGet ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">Loading triggers...</div>
            </div>
          ) : triggerState.getError ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-destructive">{triggerState.getError}</div>
            </div>
          ) : filteredTriggers.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">
                {searchQuery ? "No triggers found" : "No triggers available"}
              </div>
            </div>
          ) : (
            <div className="grid gap-2">
              {filteredTriggers.map((trigger) => (
                <Button
                  key={trigger.id}
                  variant="outline"
                  className="h-auto justify-start p-3 sm:p-4 text-left active:bg-muted/70 w-full overflow-hidden"
                  onClick={() => handleSelectTrigger(trigger)}
                >
                  <div className="flex items-start gap-2 sm:gap-3 w-full min-w-0">
                    {trigger.imageUrl && (
                      <img
                        src={trigger.imageUrl}
                        alt={trigger.name}
                        className="h-8 w-8 rounded object-contain flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="font-medium text-sm sm:text-base truncate">
                        {trigger.name}
                      </div>
                      <div className="text-xs sm:text-sm text-muted-foreground line-clamp-2">
                        {trigger.description}
                      </div>
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
